/**
 * Bread Risk Assessment Component
 * 
 * Comprehensive ESG-aligned risk assessment dashboard providing:
 * - Multi-dimensional risk analysis (ESG, operational, financial, regulatory, strategic)
 * - Risk scoring and prioritization with heat mapping and trend analysis
 * - Mitigation strategy tracking with effectiveness measurement and investment analysis
 * - Stakeholder risk evaluation with impact assessment and relationship management
 * - Scenario analysis and stress testing with predictive modeling capabilities
 * - Risk governance framework with committee oversight and reporting structures
 * - Real-time risk monitoring with alert systems and proactive risk identification
 * - Integration with compliance monitoring and governance policies systems
 * 
 * Provides comprehensive enterprise risk management capabilities aligned with ESG frameworks
 * and industry best practices for governance excellence and stakeholder value protection.
 */

import React, { useState, useEffect } from 'react';
import esgRiskAssessmentService from '../../../services/esg/esgRiskAssessmentService';
import { 
  PageHeader, 
  TabNavigation, 
  KPIGrid, 
  ChartCard, 
  InsightBanner, 
  SummaryCard 
} from '../../../components/shared';
import { useDashboardData, useTimeframe } from '../../../hooks';

export default function BreadRiskAssessment() {
  // State management for different sections
  const [activeTab, setActiveTab] = useState('overview');
  const [riskData, setRiskData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterRiskLevel, setFilterRiskLevel] = useState('all');

  // Load risk assessment data on component mount
  useEffect(() => {
    const loadRiskData = async () => {
      try {
        const [
          overview,
          esgRisks,
          operationalRisks,
          financialRisks,
          heatMap,
          mitigationStrategies,
          trends,
          stakeholderRisks,
          scenarioAnalysis,
          governance,
          integration,
          recommendations
        ] = await Promise.all([
          esgRiskAssessmentService.getRiskOverview(),
          esgRiskAssessmentService.getESGRisks(),
          esgRiskAssessmentService.getOperationalRisks(),
          esgRiskAssessmentService.getFinancialRisks(),
          esgRiskAssessmentService.getRiskHeatMap(),
          esgRiskAssessmentService.getMitigationStrategies(),
          esgRiskAssessmentService.getRiskTrends(),
          esgRiskAssessmentService.getStakeholderRisks(),
          esgRiskAssessmentService.getScenarioAnalysis(),
          esgRiskAssessmentService.getRiskGovernance(),
          esgRiskAssessmentService.getSystemIntegration(),
          esgRiskAssessmentService.getRecommendations()
        ]);

        setRiskData({
          overview: overview.data,
          esgRisks: esgRisks.data,
          operationalRisks: operationalRisks.data,
          financialRisks: financialRisks.data,
          heatMap: heatMap.data,
          mitigationStrategies: mitigationStrategies.data,
          trends: trends.data,
          stakeholderRisks: stakeholderRisks.data,
          scenarioAnalysis: scenarioAnalysis.data,
          governance: governance.data,
          integration: integration.data,
          recommendations: recommendations.data
        });
      } catch (error) {
        console.error('Error loading risk assessment data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRiskData();

    // Subscribe to real-time updates
    const unsubscribe = esgRiskAssessmentService.subscribe((updatedData) => {
      setRiskData(prev => ({ ...prev, ...updatedData }));
    });

    return unsubscribe;
  }, []);

  // Get risk level color
  const getRiskLevelColor = (level) => {
    if (typeof level === 'number') {
      if (level >= 4) return 'text-red-600 bg-red-50 border-red-200';
      if (level >= 3) return 'text-orange-600 bg-orange-50 border-orange-200';
      if (level >= 2) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      return 'text-green-600 bg-green-50 border-green-200';
    }
    switch (level) {
      case 'very-high': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'controlled': return 'text-green-600 bg-green-50 border-green-200';
      case 'monitoring': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'active': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // Filter risks based on search and filters
  const filterRisks = (risks) => {
    if (!risks) return [];
    return risks.filter(risk => {
      const matchesSearch = risk.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           risk.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === 'all' || risk.category === filterCategory;
      const riskScore = risk.inherentRisk || risk.riskScore;
      const matchesRiskLevel = filterRiskLevel === 'all' || 
                              (filterRiskLevel === 'high' && riskScore >= 3.5) ||
                              (filterRiskLevel === 'medium' && riskScore >= 2.5 && riskScore < 3.5) ||
                              (filterRiskLevel === 'low' && riskScore < 2.5);
      return matchesSearch && matchesCategory && matchesRiskLevel;
    });
  };

  // Get all risks combined
  const getAllRisks = () => {
    if (!riskData) return [];
    return [
      ...(riskData.esgRisks || []),
      ...(riskData.operationalRisks || []),
      ...(riskData.financialRisks || [])
    ];
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-black">Loading risk assessment data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!riskData) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
          <p className="text-center text-black">Unable to load risk assessment data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6" style={{ color: 'black' }}>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-black mb-2">ESG Risk Assessment</h1>
        <p className="text-black">
          Experience our Comprehensive ESG Risk Snapshot. This dashboard offers a dynamic view into quantum-powered processing that helps builds trust-based insurance for every kind of user. Whether you're shaping ESG initiatives, managing investment risk, or driving impact, this system harmonizes complexity into clarity, so every move is backed by integrity and designed for resilience.
        </p>
      </div>

      {/* Key Risk Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="text-sm text-black mb-1">Overall Risk Score</div>
          <div className="text-2xl font-bold text-black">{riskData.overview?.overallRiskScore}/5</div>
          <div className={`text-xs ${riskData.overview?.riskTrend === 'improving' ? 'text-green-600' : 'text-gray-600'}`}>
            {riskData.overview?.riskTrend === 'improving' ? '↓ Improving' : '→ Stable'}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="text-sm text-black mb-1">Total Risks</div>
          <div className="text-2xl font-bold text-black">{riskData.overview?.totalRisks}</div>
          <div className="text-xs text-black">Across all categories</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="text-sm text-black mb-1">High Risk Items</div>
          <div className="text-2xl font-bold text-red-600">{riskData.overview?.highRisks}</div>
          <div className="text-xs text-black">Require immediate attention</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="text-sm text-black mb-1">Mitigation Effectiveness</div>
          <div className="text-2xl font-bold text-black">{riskData.overview?.mitigationEffectiveness}%</div>
          <div className="text-xs text-green-600">Above target</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="text-sm text-black mb-1">Active Mitigations</div>
          <div className="text-2xl font-bold text-black">{riskData.mitigationStrategies?.implemented || 0}</div>
          <div className="text-xs text-black">{riskData.mitigationStrategies?.inProgress || 0} in progress</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="text-sm text-black mb-1">Investment</div>
          <div className="text-2xl font-bold text-black">${riskData.mitigationStrategies?.totalInvestment}M</div>
          <div className="text-xs text-black">Risk mitigation spend</div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', label: 'Risk Overview' },
            { id: 'esg', label: 'ESG Risks' },
            { id: 'operational', label: 'Operational Risks' },
            { id: 'financial', label: 'Financial Risks' },
            { id: 'mitigation', label: 'Mitigation Strategies' },
            { id: 'stakeholder', label: 'Stakeholder Risks' },
            { id: 'scenario', label: 'Scenario Analysis' },
            { id: 'governance', label: 'Risk Governance' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-black hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* Risk Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Risk Distribution */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-black mb-4">Risk Distribution</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-black">High Risk</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-32 bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-red-600 h-3 rounded-full" 
                        style={{ width: `${(riskData.overview.highRisks / riskData.overview.totalRisks) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-black font-medium w-8">{riskData.overview.highRisks}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-black">Medium Risk</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-32 bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-yellow-600 h-3 rounded-full" 
                        style={{ width: `${(riskData.overview.mediumRisks / riskData.overview.totalRisks) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-black font-medium w-8">{riskData.overview.mediumRisks}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-black">Low Risk</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-32 bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-green-600 h-3 rounded-full" 
                        style={{ width: `${(riskData.overview.lowRisks / riskData.overview.totalRisks) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-black font-medium w-8">{riskData.overview.lowRisks}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Risk Trends */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-black mb-4">Risk Score Trends</h3>
              <div className="space-y-3">
                {riskData.trends?.monthlyScores?.map((item, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-sm text-black">{item.month}</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-black">{item.score}</span>
                      <div className="text-xs text-black">
                        +{item.newRisks} -{item.closedRisks}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Heat Map Summary */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 lg:col-span-2">
              <h3 className="text-lg font-semibold text-black mb-4">Risk Heat Map</h3>
              <div className="grid grid-cols-5 gap-4">
                {Object.entries(riskData.heatMap || {}).map(([level, data]) => (
                  <div key={level} className={`p-4 rounded-lg border ${getRiskLevelColor(level)} text-center`}>
                    <div className="font-semibold text-lg">{data.count}</div>
                    <div className="text-xs capitalize">{level.replace(/([A-Z])/g, ' $1').trim()}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ESG Risks Tab */}
        {activeTab === 'esg' && (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-black">ESG Risk Assessment</h3>
              <div className="flex space-x-4">
                <input
                  type="text"
                  placeholder="Search risks..."
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm text-black"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <select
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm text-black"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  <option value="Environmental">Environmental</option>
                  <option value="Social">Social</option>
                  <option value="Governance">Governance</option>
                </select>
              </div>
            </div>

            {/* ESG Risk Categories */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {['Environmental', 'Social', 'Governance'].map(category => {
                const categoryRisks = riskData.esgRisks?.filter(risk => risk.category === category);
                const avgRisk = categoryRisks?.reduce((sum, risk) => sum + risk.inherentRisk, 0) / (categoryRisks?.length || 1);
                return (
                  <div key={category} className="border border-slate-200 rounded-lg p-4">
                    <h4 className="font-medium text-black mb-2">{category}</h4>
                    <div className="text-2xl font-bold text-black mb-1">{avgRisk.toFixed(1)}/5</div>
                    <div className="text-sm text-black">{categoryRisks?.length || 0} risks identified</div>
                  </div>
                );
              })}
            </div>

            {/* ESG Risk Details */}
            <div className="space-y-4">
              {filterRisks(riskData.esgRisks).map((risk, index) => (
                <div key={index} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h4 className="font-medium text-black">{risk.title}</h4>
                      <p className="text-sm text-black mt-1">{risk.description}</p>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        risk.category === 'Environmental' ? 'text-green-700 bg-green-100' :
                        risk.category === 'Social' ? 'text-blue-700 bg-blue-100' :
                        'text-purple-700 bg-purple-100'
                      }`}>
                        {risk.category}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRiskLevelColor(risk.inherentRisk)}`}>
                        Risk: {risk.inherentRisk}/5
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(risk.status)}`}>
                        {risk.status}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                    <div>
                      <span className="text-black font-medium">Owner:</span>
                      <div className="text-black">{risk.owner}</div>
                    </div>
                    <div>
                      <span className="text-black font-medium">Impact:</span>
                      <div className="text-black capitalize">{risk.impact}</div>
                    </div>
                    <div>
                      <span className="text-black font-medium">Residual Risk:</span>
                      <div className="text-black">{risk.residualRisk}/5</div>
                    </div>
                    <div>
                      <span className="text-black font-medium">Financial Impact:</span>
                      <div className="text-black">${risk.financialImpact}M</div>
                    </div>
                  </div>
                  <div className="border-t border-slate-200 pt-3">
                    <span className="text-black font-medium">Mitigation Strategies:</span>
                    <ul className="text-sm text-black mt-1 list-disc list-inside">
                      {risk.mitigationStrategies?.map((strategy, idx) => (
                        <li key={idx}>{strategy}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Operational Risks Tab */}
        {activeTab === 'operational' && (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-black mb-4">Operational Risk Assessment</h3>
            <div className="space-y-4">
              {riskData.operationalRisks?.map((risk, index) => (
                <div key={index} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-medium text-black">{risk.title}</h4>
                      <p className="text-sm text-black mt-1">{risk.description}</p>
                    </div>
                    <div className="flex space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRiskLevelColor(risk.riskScore)}`}>
                        {risk.riskScore}/5
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(risk.status)}`}>
                        {risk.status}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-black font-medium">Category:</span>
                      <div className="text-black">{risk.category}</div>
                    </div>
                    <div>
                      <span className="text-black font-medium">Owner:</span>
                      <div className="text-black">{risk.owner}</div>
                    </div>
                    <div>
                      <span className="text-black font-medium">Mitigation Progress:</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${risk.mitigationProgress}%` }}
                          ></div>
                        </div>
                        <span className="text-black">{risk.mitigationProgress}%</span>
                      </div>
                    </div>
                    <div>
                      <span className="text-black font-medium">Last Incident:</span>
                      <div className="text-black">{new Date(risk.lastIncident).toLocaleDateString()}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Financial Risks Tab */}
        {activeTab === 'financial' && (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-black mb-4">Financial Risk Assessment</h3>
            <div className="space-y-4">
              {riskData.financialRisks?.map((risk, index) => (
                <div key={index} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-medium text-black">{risk.title}</h4>
                      <p className="text-sm text-black mt-1">{risk.description}</p>
                    </div>
                    <div className="flex space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRiskLevelColor(risk.riskScore)}`}>
                        {risk.riskScore}/5
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(risk.status)}`}>
                        {risk.status}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-black font-medium">Category:</span>
                      <div className="text-black">{risk.category}</div>
                    </div>
                    <div>
                      <span className="text-black font-medium">Owner:</span>
                      <div className="text-black">{risk.owner}</div>
                    </div>
                    <div>
                      <span className="text-black font-medium">Exposure:</span>
                      <div className="text-black">${risk.exposureAmount}M</div>
                    </div>
                    <div>
                      <span className="text-black font-medium">Mitigation Progress:</span>
                      <div className="text-black">{risk.mitigationProgress}%</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mitigation Strategies Tab */}
        {activeTab === 'mitigation' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Mitigation Overview */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-black mb-4">Mitigation Overview</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 border border-green-200 rounded-lg bg-green-50">
                    <div className="text-xl font-bold text-green-600">{riskData.mitigationStrategies?.implemented}</div>
                    <div className="text-xs text-black">Implemented</div>
                  </div>
                  <div className="text-center p-3 border border-yellow-200 rounded-lg bg-yellow-50">
                    <div className="text-xl font-bold text-yellow-600">{riskData.mitigationStrategies?.inProgress}</div>
                    <div className="text-xs text-black">In Progress</div>
                  </div>
                  <div className="text-center p-3 border border-blue-200 rounded-lg bg-blue-50">
                    <div className="text-xl font-bold text-blue-600">{riskData.mitigationStrategies?.planned}</div>
                    <div className="text-xs text-black">Planned</div>
                  </div>
                </div>
                <div className="pt-4 border-t border-slate-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-black font-medium">Average Effectiveness</span>
                    <span className="text-black font-bold">{riskData.mitigationStrategies?.averageEffectiveness}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-blue-600 h-3 rounded-full" 
                      style={{ width: `${riskData.mitigationStrategies?.averageEffectiveness}%` }}
                    ></div>
                  </div>
                </div>
                <div className="pt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-black font-medium">Total Investment</span>
                    <span className="text-black font-bold">${riskData.mitigationStrategies?.totalInvestment}M</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Strategies by Category */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-black mb-4">Strategies by Category</h3>
              <div className="space-y-4">
                {Object.entries(riskData.mitigationStrategies?.strategiesByCategory || {}).map(([category, data]) => (
                  <div key={category} className="border border-slate-200 rounded-lg p-3">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-black">{category}</h4>
                      <span className="text-sm text-black">{data.effectiveness}%</span>
                    </div>
                    <div className="text-sm text-black space-y-1">
                      <div>Strategies: {data.count}</div>
                      <div>Investment: ${data.investment}M</div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full" 
                        style={{ width: `${data.effectiveness}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Stakeholder Risks Tab */}
        {activeTab === 'stakeholder' && (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-black mb-4">Stakeholder Risk Assessment</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(riskData.stakeholderRisks || {}).map(([stakeholder, data]) => (
                <div key={stakeholder} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium text-black capitalize">{stakeholder}</h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRiskLevelColor(data.riskLevel)}`}>
                      {data.riskLevel}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm text-black font-medium">Key Risks:</span>
                      <ul className="text-xs text-black list-disc list-inside mt-1">
                        {data.keyRisks?.map((risk, idx) => (
                          <li key={idx}>{risk}</li>
                        ))}
                      </ul>
                    </div>
                    {data.satisfactionScore && (
                      <div>
                        <span className="text-sm text-black font-medium">Satisfaction: </span>
                        <span className="text-sm text-black">{data.satisfactionScore}/10</span>
                      </div>
                    )}
                    {data.engagementScore && (
                      <div>
                        <span className="text-sm text-black font-medium">Engagement: </span>
                        <span className="text-sm text-black">{data.engagementScore}/10</span>
                      </div>
                    )}
                    {data.complianceScore && (
                      <div>
                        <span className="text-sm text-black font-medium">Compliance: </span>
                        <span className="text-sm text-black">{data.complianceScore}%</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scenario Analysis Tab */}
        {activeTab === 'scenario' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Scenarios */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-black mb-4">Scenario Analysis</h3>
              <div className="space-y-4">
                {riskData.scenarioAnalysis?.scenarios?.map((scenario, index) => (
                  <div key={index} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-black">{scenario.name}</h4>
                      <span className="text-sm text-black">{(scenario.probability * 100).toFixed(0)}% probability</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-black font-medium">Impact Score:</span>
                        <div className="text-black">{scenario.impactScore}/5</div>
                      </div>
                      <div>
                        <span className="text-black font-medium">Estimated Loss:</span>
                        <div className="text-black">${scenario.estimatedLoss}M</div>
                      </div>
                      <div>
                        <span className="text-black font-medium">Affected Risks:</span>
                        <div className="text-black">{scenario.affectedRisks}</div>
                      </div>
                      <div>
                        <span className="text-black font-medium">Readiness:</span>
                        <div className="text-black">{scenario.mitigationReadiness}%</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stress Testing */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-black mb-4">Stress Testing Results</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 border border-slate-200 rounded-lg">
                    <div className="text-xl font-bold text-black">{riskData.scenarioAnalysis?.stressTesting?.passRate}%</div>
                    <div className="text-xs text-black">Pass Rate</div>
                  </div>
                  <div className="text-center p-3 border border-red-200 rounded-lg bg-red-50">
                    <div className="text-xl font-bold text-red-600">{riskData.scenarioAnalysis?.stressTesting?.criticalFailures}</div>
                    <div className="text-xs text-black">Critical Failures</div>
                  </div>
                </div>
                <div>
                  <span className="text-black font-medium">Last Test:</span>
                  <div className="text-black">{new Date(riskData.scenarioAnalysis?.stressTesting?.lastTest).toLocaleDateString()}</div>
                </div>
                <div>
                  <span className="text-black font-medium">Next Test:</span>
                  <div className="text-black">{new Date(riskData.scenarioAnalysis?.stressTesting?.nextTest).toLocaleDateString()}</div>
                </div>
                <div>
                  <span className="text-black font-medium">Improvement Areas:</span>
                  <ul className="text-sm text-black list-disc list-inside mt-1">
                    {riskData.scenarioAnalysis?.stressTesting?.improvementAreas?.map((area, idx) => (
                      <li key={idx}>{area}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Risk Governance Tab */}
        {activeTab === 'governance' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Risk Committee */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-black mb-4">Risk Committee</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 border border-slate-200 rounded-lg">
                    <div className="text-xl font-bold text-black">{riskData.governance?.riskCommittee?.attendanceRate}%</div>
                    <div className="text-xs text-black">Attendance Rate</div>
                  </div>
                  <div className="text-center p-3 border border-slate-200 rounded-lg">
                    <div className="text-xl font-bold text-black">{riskData.governance?.riskCommittee?.implementationRate}%</div>
                    <div className="text-xs text-black">Implementation Rate</div>
                  </div>
                </div>
                <div>
                  <span className="text-black font-medium">Decisions Tracked:</span>
                  <div className="text-black">{riskData.governance?.riskCommittee?.decisionsTracked}</div>
                </div>
                <div>
                  <span className="text-black font-medium">Last Meeting:</span>
                  <div className="text-black">{new Date(riskData.governance?.riskCommittee?.lastMeeting).toLocaleDateString()}</div>
                </div>
                <div>
                  <span className="text-black font-medium">Next Meeting:</span>
                  <div className="text-black">{new Date(riskData.governance?.riskCommittee?.nextMeeting).toLocaleDateString()}</div>
                </div>
              </div>
            </div>

            {/* Risk Appetite & Reporting */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-black mb-4">Risk Appetite & Reporting</h3>
              <div className="space-y-4">
                <div>
                  <span className="text-black font-medium">Current Risk Appetite:</span>
                  <div className="text-black capitalize">{riskData.governance?.riskAppetite?.currentLevel}</div>
                </div>
                <div>
                  <span className="text-black font-medium">Target Level:</span>
                  <div className="text-black capitalize">{riskData.governance?.riskAppetite?.targetLevel}</div>
                </div>
                <div>
                  <span className="text-black font-medium">Risk Tolerance:</span>
                  <div className="text-black capitalize">{riskData.governance?.riskAppetite?.tolerance}</div>
                </div>
                <div className="pt-3 border-t border-slate-200">
                  <h4 className="font-medium text-black mb-2">Reporting Summary</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>Executive Reports: {riskData.governance?.reporting?.executiveReports}</div>
                    <div>Board Reports: {riskData.governance?.reporting?.boardReports}</div>
                    <div>Regulatory Reports: {riskData.governance?.reporting?.regulatoryReports}</div>
                    <div>Stakeholder Updates: {riskData.governance?.reporting?.stakeholderUpdates}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recommendations Section */}
      {riskData.recommendations && (
        <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-black mb-4">Risk Management Recommendations</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Immediate Actions */}
            <div>
              <h4 className="font-medium text-red-700 mb-2">Immediate (Critical)</h4>
              <div className="space-y-3">
                {riskData.recommendations.immediate?.map((rec, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 border border-red-200">
                    <div className="font-medium text-black text-sm">{rec.action}</div>
                    <div className="text-xs text-black mt-1">{rec.category}</div>
                    <div className="text-xs text-black">Investment: ${(rec.investment/1000).toFixed(0)}K</div>
                    <div className="text-xs text-black">Timeline: {rec.timeline}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Short-term Actions */}
            <div>
              <h4 className="font-medium text-orange-700 mb-2">Short-term (High Priority)</h4>
              <div className="space-y-3">
                {riskData.recommendations.shortTerm?.map((rec, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 border border-orange-200">
                    <div className="font-medium text-black text-sm">{rec.action}</div>
                    <div className="text-xs text-black mt-1">{rec.category}</div>
                    <div className="text-xs text-black">Investment: ${(rec.investment/1000).toFixed(0)}K</div>
                    <div className="text-xs text-black">Timeline: {rec.timeline}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Long-term Actions */}
            <div>
              <h4 className="font-medium text-blue-700 mb-2">Long-term (Strategic)</h4>
              <div className="space-y-3">
                {riskData.recommendations.longTerm?.map((rec, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 border border-blue-200">
                    <div className="font-medium text-black text-sm">{rec.action}</div>
                    <div className="text-xs text-black mt-1">{rec.category}</div>
                    <div className="text-xs text-black">Investment: ${(rec.investment/1000).toFixed(0)}K</div>
                    <div className="text-xs text-black">Timeline: {rec.timeline}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}